import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/hooks/useAppSelector';

import {
  TransportationAttribute,
  TransportationAttributeGroup,
} from '@/data/models/transportation-attribute.model';

import { selectTransportationAttributes } from '@/data/accounting.slice';
import {
  selectReverseTransportationAttributeMap,
  selectTransportationAttributeMap,
} from '@/data/lookup.slice';
import { selectCurrentPassengerPreferences } from '@/data/passenger.slice';

import { PassengerAssistanceField } from './PassengerAssistanceField';
import { PassengerMobilityField } from './PassengerMobilityField';

import { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import type { LevelOfCareContext } from '../machines/levelOfCare.machine';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import reservationUtils from '@/services/reservationUtils';
import { BARIATRIC_GURNEY_BC, GURNEY_BC, R2R_BC } from '@/data/constants';
import { useFeatureMemberPreferences } from '@/hooks/features/useFeatureMemberPreferences';

export type AttributesLevels = {
  level1: TransportationAttribute[];
  level2: TransportationAttribute[] | undefined;
  level3: TransportationAttribute[] | undefined;
  level4: TransportationAttribute[] | undefined;
  level5: TransportationAttribute[] | undefined;
  level6: TransportationAttribute[] | undefined;
  assistance: TransportationAttribute[] | undefined;
};

export function LevelOfCareSection({ index }: { index: number }) {
  const { t } = useTranslation('common');
  const { control, setValue } = useFormContext<ReservationFormInput>();
  const { touchedFields } = useFormState({ control });
  const tripTouched = touchedFields.trips?.[index];
  const heightTouched = Boolean(tripTouched?.height);
  const weightTouched = Boolean(tripTouched?.weight);
  const { fieldRefs } = useReservationFormMachines();
  const levelOfCareActor = fieldRefs.levelOfCare;
  const levelOfCareSnap = useFieldActorSnapshot(levelOfCareActor);
  const {
    exceedsMeasurementsLimitsByIndex,
    withinMeasurementsLimitsByIndex,
    attributeLevel2Values,
    mobilityAttributeValues,
  } = levelOfCareSnap.context as LevelOfCareContext;
  const machineAttributeLevel2 = attributeLevel2Values[index] ?? '';
  const machineMobilityAttribute = mobilityAttributeValues[index] ?? '';

  const transportationAttributes = useAppSelector(
    selectTransportationAttributes,
  );
  const transportationAttributeMap = useAppSelector(
    selectTransportationAttributeMap,
  );
  const reverseTransportationAttributeMap = useAppSelector(
    selectReverseTransportationAttributeMap,
  );

  // Reverse attribute lookups for later use
  const gurneyAttribute = reverseTransportationAttributeMap[GURNEY_BC];
  const bariatricGurneyAttribute =
    reverseTransportationAttributeMap[BARIATRIC_GURNEY_BC];

  // Watch form values here so we can send them to the state machine
  const attributeLevel1 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel1`,
    }) ?? '';
  const attributeLevel2 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel2`,
    }) ?? '';
  const attributeLevel3 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel3`,
    }) ?? '';
  const attributeLevel4 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel4`,
    }) ?? '';
  const attributeLevel5 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel5`,
    }) ?? '';
  const attributeLevel6 =
    useWatch({
      control,
      name: `trips.${index}.AttributeLevel6`,
    }) ?? '';
  const mobilityAttribute =
    useWatch({
      control,
      name: `trips.${index}.MobilityAttribute`,
    }) ?? '';

  const height =
    useWatch({
      control,
      name: `trips.${index}.height`,
    }) ?? '';
  const weight =
    useWatch({
      control,
      name: `trips.${index}.weight`,
    }) ?? '';

  // Construct the available attributes to populate all of the form fields
  const availableAttributes: AttributesLevels = {
    level1: [],
    level2: [],
    level3: [],
    level4: [],
    level5: [],
    level6: [],
    assistance: [],
  };

  const setChildAttribute = (
    parentUUID: string | undefined,
    all: TransportationAttribute[],
  ): TransportationAttribute[] | undefined => {
    if (!parentUUID) {
      return undefined;
    }

    const parent = all.find(
      (ta) => ta.transportationAttributeUUID === parentUUID,
    );
    if (!parent?.childTransportationAttributeCategoryBC) {
      return undefined;
    }
    return all.filter(
      (ta) =>
        ta.transportationAttributeCategoryBC ===
        parent?.childTransportationAttributeCategoryBC,
    );
  };

  // Compute each available attribute level options in succession
  availableAttributes.level1 =
    transportationAttributes?.filter(
      (a) =>
        a.grouping === TransportationAttributeGroup.PRIMARY &&
        a.isPassengerSelectable,
    ) ?? availableAttributes.level1;

  // Attribute level 2 has some special gurney logic
  availableAttributes.level2 = useMemo(() => {
    const attrs = setChildAttribute(attributeLevel1, transportationAttributes);
    // If in member portal, filter out Gurney and Bariatric Gurney options

    // If measurements exceed limits, filter out gurney option
    if (exceedsMeasurementsLimitsByIndex[index]) {
      return attrs?.filter(
        (attr) => attr.transportationAttributeUUID !== gurneyAttribute,
      );
    }
    // If measurements within limits, filter out bariatric gurney option
    if (withinMeasurementsLimitsByIndex[index]) {
      return attrs?.filter(
        (attr) => attr.transportationAttributeUUID !== bariatricGurneyAttribute,
      );
    }
    return attrs;
  }, [
    attributeLevel1,
    transportationAttributes,
    exceedsMeasurementsLimitsByIndex,
    withinMeasurementsLimitsByIndex,
    index,
    gurneyAttribute,
    bariatricGurneyAttribute,
  ]);

  availableAttributes.level3 = useMemo(
    () => setChildAttribute(attributeLevel2, transportationAttributes),
    [attributeLevel2, transportationAttributes],
  );

  availableAttributes.level4 = useMemo(
    () => setChildAttribute(attributeLevel3, transportationAttributes),
    [attributeLevel3, transportationAttributes],
  );

  availableAttributes.level5 = useMemo(
    () => setChildAttribute(attributeLevel4, transportationAttributes),
    [attributeLevel4, transportationAttributes],
  );

  availableAttributes.level6 = useMemo(
    () => setChildAttribute(attributeLevel5, transportationAttributes),
    [attributeLevel5, transportationAttributes],
  );

  availableAttributes.assistance = useMemo(
    () =>
      transportationAttributes?.filter(
        (a) =>
          a.transportationAttributeCategoryBC ===
          reservationUtils.MobilityAttributeBC,
      ) ?? availableAttributes.assistance,
    [transportationAttributes],
  );

  const r2rMobilityAttributeUuid =
    transportationAttributes?.find(
      (attr) => attr.passengerCareLevelBC === R2R_BC,
    )?.transportationAttributeUUID ?? null;

  const memberPreferencesEnabled = useFeatureMemberPreferences();
  const passengerPreferences = useAppSelector(
    selectCurrentPassengerPreferences,
  );
  // We need send this to the state machine because it's used to compute the
  // non-ambulatory and gurney flags
  useEffect(() => {
    levelOfCareActor.send({
      type: 'SET_TRANSPORTATION_ATTRIBUTE_MAP',
      map: transportationAttributeMap ?? null,
      reverseMap: reverseTransportationAttributeMap ?? null,
      r2rMobilityAttributeUuid,
      passengerPreferences,
      memberPreferencesEnabled,
    });
  }, [
    levelOfCareActor,
    transportationAttributeMap,
    reverseTransportationAttributeMap,
    r2rMobilityAttributeUuid,
    passengerPreferences,
    memberPreferencesEnabled,
  ]);

  // Send all attribute level changes to the state machine
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 1,
      value: attributeLevel1,
    });
  }, [levelOfCareActor, index, attributeLevel1]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 2,
      value: attributeLevel2,
    });
  }, [levelOfCareActor, index, attributeLevel2]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 3,
      value: attributeLevel3,
    });
  }, [levelOfCareActor, index, attributeLevel3]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 4,
      value: attributeLevel4,
    });
  }, [levelOfCareActor, index, attributeLevel4]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 5,
      value: attributeLevel5,
    });
  }, [levelOfCareActor, index, attributeLevel5]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'ATTRIBUTE_LEVEL_CHANGE',
      index,
      level: 6,
      value: attributeLevel6,
    });
  }, [levelOfCareActor, index, attributeLevel6]);
  useEffect(() => {
    levelOfCareActor.send({
      type: 'MOBILITY_ATTRIBUTE_CHANGE',
      index,
      value: mobilityAttribute,
    });
  }, [levelOfCareActor, index, mobilityAttribute]);

  useEffect(() => {
    levelOfCareActor.send({
      type: 'MEASUREMENTS_CHANGE',
      index,
      height: String(height),
      weight: String(weight),
      heightTouched,
      weightTouched,
    });
  }, [levelOfCareActor, index, height, weight, heightTouched, weightTouched]);

  // Keep the AttributeLevel2 Select in sync when the machine derives gurney vs bariatric from measurements.
  useEffect(() => {
    const fromMachine = (
      levelOfCareActor.getSnapshot().context as LevelOfCareContext
    ).attributeLevel2Values[index];
    const next = fromMachine ?? '';
    if (!next || next === attributeLevel2) return;
    setValue(`trips.${index}.AttributeLevel2`, next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    const switchingBetweenGurneyTypes =
      (attributeLevel2 === gurneyAttribute || attributeLevel2 === bariatricGurneyAttribute) &&
      (next === gurneyAttribute || next === bariatricGurneyAttribute);
    for (let level = 3; level <= 6; level++) {
      if (switchingBetweenGurneyTypes && (level === 4 || level === 5)) continue;
      setValue(
        `trips.${index}.AttributeLevel${level}` as FieldPath<ReservationFormInput>,
        '',
        { shouldValidate: true, shouldDirty: true },
      );
    }
  }, [
    levelOfCareActor,
    index,
    attributeLevel2,
    height,
    weight,
    machineAttributeLevel2,
    setValue,
  ]);

  // When gurney is selected, the machine sets MobilityAttribute to the R2R transportation attribute.
  useEffect(() => {
    const fromMachine =
      (levelOfCareActor.getSnapshot().context as LevelOfCareContext)
        .mobilityAttributeValues[index] ?? '';
    if (!fromMachine || fromMachine === mobilityAttribute) return;
    setValue(`trips.${index}.MobilityAttribute`, fromMachine, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [
    levelOfCareActor,
    index,
    mobilityAttribute,
    machineMobilityAttribute,
    setValue,
  ]);

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.up('md')]: {
          width: '75%',
        },
      })}>
      <Typography variant="h5" component="h3">
        {t('member.levelOfCare')}
      </Typography>
      <Box
        component="fieldset"
        sx={(theme) => ({
          border: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}>
        <PassengerMobilityField
          index={index}
          availableAttributes={availableAttributes}
        />
        <PassengerAssistanceField
          index={index}
          availableAttributes={availableAttributes}
        />
      </Box>
    </Box>
  );
}
